import { computed, Injectable, signal } from '@angular/core';
import { inject } from '@angular/core/primitives/di';
import { Observable } from 'rxjs';
import { OtpService } from '../../../shared/services/otp.service';

export type ScreenType = 'CREDENTIAL' | 'OTP' | 'PASSWORD';

export const SCREEN_TYPE = {
  CREDENTIAL: 'CREDENTIAL',
  OTP: 'OTP',
  PASSWORD: 'PASSWORD',
} as const;

export type RegistrationInfo = {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  otp?: string;
  otpToken?: string;
  password?: string;
  otpSessionId?: string;
  signUpReferralCode?: string;
  otpLength?: number;
  resendIntervalInSeconds?: number;
};

@Injectable({
  providedIn: 'root',
})
export class SignUpService {
  readonly currentScreen = signal<ScreenType>(SCREEN_TYPE.CREDENTIAL);
  readonly userDetails = signal<RegistrationInfo>(null);
  readonly hasCredentialState = computed(() => {
    const details = this.userDetails();
    return !!details?.fullName && !!details?.phoneNumber;
  });

  private readonly otpService = inject(OtpService);

  updateScreen(screenType: ScreenType): void {
    this.currentScreen.set(screenType);
  }

  sendOtp(): Observable<any> {
    const phoneNumber = this.userDetails()?.phoneNumber;
    return this.otpService.sendOtp(phoneNumber);
  }

  validateOtp(otp: string): Observable<any> {
    const userDetails = this.userDetails();
    return this.otpService.validateOtp(otp, userDetails?.otpToken, userDetails?.phoneNumber);
  }

  validateLinkedAccount(otpToken: string, sid: string): Observable<any> {
    return this.otpService.validateLinkedAccount({
      otpToken: otpToken,
      linkedAccountSID: sid,
    });
  }

  resendOtp(): Observable<any> {
    const userDetails = this.userDetails();
    return this.otpService.resendOtp(userDetails?.phoneNumber);
  }

  hasOtpState(): boolean {
    const details = this.userDetails();
    return this.hasCredentialState() && !!details?.otpToken;
  }

  hasPasswordState(): boolean {
    const details = this.userDetails();
    return this.hasOtpState() && !!details?.otpSessionId;
  }

  reset(): void {
    this.currentScreen.set(SCREEN_TYPE.CREDENTIAL);
    this.userDetails.set(null);
  }
}
